import { ProgressiveImage } from "@/components/ProgressiveImage"
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useHorizontalDrag } from '@/components/property/useHorizontalDrag'
import {
  Bath,
  BedDouble,
  BookOpenText,
  Building2,
  CalendarDays,
  CarFront,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Home,
  KeyRound,
  Loader2,
  MapPin,
  Megaphone,
  MessageCircle,
  Newspaper,
  Ruler,
  Search,
  Sparkles,
  UsersRound,
} from 'lucide-react'
import { collection, onSnapshot, query, pageOptions } from '@/lib/property-data'
import { PropertySiteFooter, PropertySiteHeader } from '@/components/property/PropertySiteChrome'
import { PropertyFaqList } from '@/components/property/PropertyFaqList'
import { HOME_PROPERTY_FAQS } from '@/data/property-faq'
import { SmartPropertyImage } from '@/components/property/SmartPropertyImage'
import { PROPERTY_TYPE_MENU_ITEMS } from '@/data/property-mega-menu'
import { PROPERTY_AGENT_BILLING } from '@/data/property-listing-config'
import { propertyDb } from '@/lib/property-data'

import type { AppLocale } from '@/lib/i18n'
import { handleDelegatedInternalNavigation, handlePropertyRegisterModalNavigation } from '@/lib/navigation'
import {
  estimatePropertyArticleReadMinutes,
  formatPropertyArticleDate,
  getPropertyArticleSlug,
  listPropertyArticles,
  type PropertyArticle,
} from '@/lib/property-articles'
import { loadPropertyAdBanners, type PropertyAdBannerAccent, type PropertyAdBannerIconKey } from '@/lib/property-ad-banners'
import { loadPropertyHomeHeroSlides, type PropertyHeroSlideIconKey } from '@/lib/property-hero-slides'
import { DEFAULT_SEO_IMAGE_URL, seoSchemas, usePageSeo } from '@/lib/seo'
import './home-landing-page.scss'
import './home-refinements.scss'

type HomeLandingPageProps = {
  locale: AppLocale
  isDark: boolean
  onToggleTheme: () => void
  onLocaleChange: (nextLocale: AppLocale) => void
}

type PropertyHeroSlide = {
  renderMode: 'image' | 'canvas'
  imageUrl: string
  badge: string
  title: string
  description: string
  href: string
  actionLabel: string
  accent: 'green' | 'blue' | 'orange' | 'white'
  stats: Array<{ label: string; value: string }>
  iconKey: PropertyHeroSlideIconKey
}

type PropertyAdBanner = {
  renderMode: 'image' | 'canvas'
  imageUrl: string
  badge: string
  title: string
  description: string
  href: string
  actionLabel: string
  accent: PropertyAdBannerAccent
  iconKey: PropertyAdBannerIconKey
}

type PropertySearchMode = 'jual' | 'sewa' | 'baru'
type PropertyRecentSearch = {
  label: string
  meta: string
  href: string
}

type AiSearchSuggestion = {
  title: string
  description: string
  chips: string[]
  href: string
  mode: PropertySearchMode
  propertyTypeSlug: string
  query: string
}

type PropertyTypeOption = {
  label: string
  slug: string
}

type MapboxSuggestion = {
  id: string
  text: string
  placeName: string
  category: string
}

type PropertyAgentCard = {
  id: string
  fullName: string
  photoUrl: string
  companyName: string
  companyLogoUrl: string
  phoneNumber: string
  status: string
  subscriptionStatus: string
  isVerifiedAgent: boolean
  listingLimit: number
  currentPeriodEnd: string
}

type PropertyFeaturedListingCard = {
  id: string
  slug: string
  title: string
  listingType: string
  propertyType: string
  price: number
  pricePeriod: string
  province: string
  city: string
  district: string
  address: string
  imageUrl: string
  landSize: number
  buildingSize: number
  bedrooms: number
  bathrooms: number
  carports: number
  isOfficial: boolean
  isFeatured: boolean
  featuredUntil: string
  status: string
  activeUntil: string
  createdAt: string
  updatedAt: string
  verifiedAt: string
}

const PASANG_IKLAN_WHATSAPP_TEXT = 'Halo tim Dihaw.co, saya mau tanya cara pasang iklan properti di Dihaw.co.'
const PASANG_IKLAN_WHATSAPP_URL = `https://wa.me/6285280871948?text=${encodeURIComponent(PASANG_IKLAN_WHATSAPP_TEXT)}`
const WHATSAPP_LOGO_SRC = '/wa.webp'
const PROPERTY_RECENT_SEARCH_STORAGE_KEY = 'dihaw.property.recentSearches'
const PROPERTY_USERS_COLLECTION = 'propertyUsers'
const PROPERTY_LISTINGS_COLLECTION = 'propertyListings'
const MAPBOX_PUBLIC_TOKEN = 'pk.eyJ1IjoiYWxpZGloYXciLCJhIjoiY203c3YzMnU2MW91ZjJqcHVuajNxazR2NSJ9.rGifpTRfoAjD83tgHU4w_Q'

const PROPERTY_SEARCH_MODES: Array<{ key: PropertySearchMode; label: string; basePath: string; Icon: typeof Home }> = [
  { key: 'jual', label: 'Dijual', basePath: '/jual', Icon: Home },
  { key: 'sewa', label: 'Disewa', basePath: '/sewa', Icon: KeyRound },
  { key: 'baru', label: 'Properti Baru', basePath: '/properti-baru', Icon: Sparkles },
]

const PROPERTY_TYPE_OPTIONS: PropertyTypeOption[] = [
  { label: 'Semua', slug: 'all' },
  ...PROPERTY_TYPE_MENU_ITEMS.map((item) => ({ label: item.label, slug: item.slug })),
]

const HERO_ICON_MAP = {
  search: Search,
  home: Home,
  building: Building2,
  megaphone: Megaphone,
  message: MessageCircle,
} satisfies Record<PropertyHeroSlideIconKey, typeof Home>

const CONTACT_HERO_SLIDE: PropertyHeroSlide = {
  renderMode: 'canvas',
  imageUrl: '',
  badge: 'Pasang iklan',
  title: 'Mau pasang iklan properti? Hubungi tim Dihaw.co.',
  description:
    'Ceritakan properti yang mau kamu tampilkan. Tim Dihaw.co akan bantu arahkan format listing yang rapi dan siap dibagikan.',
  href: PASANG_IKLAN_WHATSAPP_URL,
  actionLabel: 'Hubungi via WhatsApp',
  accent: 'orange',
  stats: [
    { label: 'Kontak', value: 'WhatsApp' },
    { label: 'Bantuan', value: 'Tim Dihaw.co' },
    { label: 'Iklan', value: 'Properti' },
  ],
  iconKey: 'message',
}

const HERO_POSTER_WIDTH = 1800
const HERO_POSTER_HEIGHT = 900
const AD_BANNER_WIDTH = 560
const AD_BANNER_HEIGHT = 210
const AGENT_FREE_UNTIL_LABEL = '1 Januari 2028'
const IS_AGENT_FREE_MODE = PROPERTY_AGENT_BILLING.mode === 'free'

const CONTACT_AD_BANNER: PropertyAdBanner = {
  renderMode: 'canvas',
  imageUrl: '',
  badge: 'Iklan properti',
  title: 'Mau listing kamu tampil di Dihaw.co?',
  description: 'Hubungi tim Dihaw.co untuk bantu rapikan materi iklan, kontak, dan tampilan properti.',
  href: PASANG_IKLAN_WHATSAPP_URL,
  actionLabel: 'WhatsApp',
  accent: 'green',
  iconKey: 'message',
}

const FALLBACK_AD_BANNERS: PropertyAdBanner[] = [
  CONTACT_AD_BANNER,
  {
    renderMode: 'canvas',
    imageUrl: '',
    badge: 'Spotlight',
    title: 'Tampilkan properti ke pencari yang tepat.',
    description: 'Cocok untuk rumah, ruko, tanah, gudang, villa, dan properti baru.',
    href: '/pasang-property',
    actionLabel: 'Pasang Property',
    accent: 'orange',
    iconKey: 'megaphone',
  },
  {
    renderMode: 'canvas',
    imageUrl: '',
    badge: 'Listing rapi',
    title: 'Iklan lebih jelas sebelum dibagikan.',
    description: 'Judul, lokasi, foto, dan kontak dibuat lebih mudah dibaca oleh calon buyer.',
    href: '/pasang-property',
    actionLabel: 'Mulai',
    accent: 'blue',
    iconKey: 'home',
  },
  {
    renderMode: 'canvas',
    imageUrl: '',
    badge: 'Agen & owner',
    title: 'Satu tempat untuk listing properti Indonesia.',
    description: 'Bantu calon pembeli membandingkan properti tanpa harus lompat antar chat.',
    href: '/jual',
    actionLabel: 'Lihat properti',
    accent: 'white',
    iconKey: 'search',
  },
]

const WHY_DIHAW_ITEMS = [
  {
    title: 'Cari Properti Lebih Tenang',
    description: 'Lihat listing dengan foto, spesifikasi, lokasi sekitar, dan kontak pemasang yang dibuat lebih mudah dipahami.',
    actionLabel: 'Cari Properti',
    href: '/jual',
    Icon: Search,
  },
  {
    title: 'Pasang Properti Lebih Rapi',
    description: 'Pemilik dan tim in-house bisa menyiapkan iklan properti lengkap, dibantu AI listing assistant sebelum masuk review.',
    actionLabel: 'Pasang Properti',
    href: '/pasang-property',
    Icon: Home,
  },
  {
    title: 'Mitra Properti Terarah',
    description: 'Agen, developer, dan partner pembiayaan bisa mulai membangun kanal properti yang lebih rapi bersama Dihaw.co.',
    actionLabel: 'Pelajari Partner',
    href: '/partner',
    Icon: UsersRound,
  },
  {
    title: 'Info KPR Praktis',
    description: 'Hitung estimasi cicilan KPR atau pembiayaan syariah sebelum lanjut bertanya ke pemasang properti.',
    actionLabel: 'Hitung KPR',
    href: '/kpr/simulasi-kpr',
    Icon: Building2,
  },
]

function formatRupiah(value: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(value || 0)
}

function formatCompactRupiah(value: number) {
  if (!value) return 'Harga hubungi penjual'
  if (value >= 1_000_000_000) {
    return `Rp ${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(value / 1_000_000_000)} M`
  }
  if (value >= 1_000_000) {
    return `Rp ${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(value / 1_000_000)} jt`
  }
  return formatRupiah(value)
}

function normalizeDateText(value: unknown) {
  if (!value) return ''
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    const date = value.toDate()
    return Number.isNaN(date.getTime()) ? '' : date.toISOString()
  }
  const date = new Date(String(value))
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

function readStringArray(value: unknown) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  if (typeof value === 'string') return value.split('\n').map((item) => item.trim()).filter(Boolean)
  return []
}

function buildFeaturedListingCard(id: string, data: Record<string, unknown>): PropertyFeaturedListingCard {
  const imageUrls = readStringArray(data.imageUrls)
  const imageDetails = Array.isArray(data.imageDetails) ? data.imageDetails as Array<Record<string, unknown>> : []
  const firstImageUrl = imageDetails.map((item) => String(item.url || '')).find(Boolean) || imageUrls[0] || ''
  return {
    id,
    slug: String(data.slug || ''),
    title: String(data.title || 'Properti Dihaw.co'),
    listingType: String(data.listingType || 'sale'),
    propertyType: String(data.propertyType || 'Properti'),
    price: Number(data.price || 0),
    pricePeriod: String(data.pricePeriod || 'total'),
    province: String(data.province || ''),
    city: String(data.city || ''),
    district: String(data.district || ''),
    address: String(data.address || ''),
    imageUrl: firstImageUrl,
    landSize: Number(data.landSize || 0),
    buildingSize: Number(data.buildingSize || 0),
    bedrooms: Number(data.bedrooms || 0),
    bathrooms: Number(data.bathrooms || 0),
    carports: Number(data.carports || 0),
    isOfficial: data.isOfficial === true,
    isFeatured: data.isFeatured === true,
    featuredUntil: normalizeDateText(data.featuredUntil),
    status: String(data.status || 'draft'),
    activeUntil: normalizeDateText(data.activeUntil),
    createdAt: normalizeDateText(data.createdAt),
    updatedAt: normalizeDateText(data.updatedAt),
    verifiedAt: normalizeDateText(data.verifiedAt),
  }
}


function isFeaturedListingActive(listing: PropertyFeaturedListingCard) {
  if (!listing.isFeatured || !listing.featuredUntil) return false
  if (listing.activeUntil && Date.parse(listing.activeUntil) < Date.now()) return false
  return Date.parse(listing.featuredUntil) >= Date.now()
}

function getListingTypeLabel(value: string) {
  if (value === 'rent') return 'Disewa'
  if (value === 'new') return 'Properti Baru'
  return 'Dijual'
}

function getListingHref(listing: PropertyFeaturedListingCard) {
  return `/properti/${listing.slug || listing.id}`
}

function getListingLocation(listing: Pick<PropertyFeaturedListingCard, 'district' | 'city' | 'province'>) {
  return [listing.district, listing.city, listing.province].filter(Boolean).slice(0, 2).join(', ') || 'Indonesia'
}

function getPropertyArticleExcerpt(article: PropertyArticle, maxLength = 132) {
  const source = article.summary || article.content || article.sourceNewsTitle || ''
  const normalized = source.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength).trim()}...`
}

function getPropertyArticleDateValue(article: PropertyArticle) {
  return article.updatedAt || article.createdAt
}

function buildAgentCard(id: string, data: Record<string, unknown>): PropertyAgentCard | null {
  if (String(data.accountType || '') !== 'agent') return null
  const subscription = data.agentSubscription && typeof data.agentSubscription === 'object' && !Array.isArray(data.agentSubscription)
    ? data.agentSubscription as Record<string, unknown>
    : {}
  return {
    id,
    fullName: String(data.fullName || data.displayName || 'Agent Dihaw.co'),
    photoUrl: String(data.photoUrl || ''),
    companyName: String(data.companyName || ''),
    companyLogoUrl: String(data.companyLogoUrl || ''),
    phoneNumber: String(data.phoneNumber || ''),
    status: String(data.status || 'active'),
    subscriptionStatus: String(subscription.status || ''),
    isVerifiedAgent: data.isVerifiedAgent === true || data.verifiedAgent === true,
    listingLimit: Number(subscription.listingLimit || PROPERTY_AGENT_BILLING.listingLimit),
    currentPeriodEnd: normalizeDateText(subscription.currentPeriodEnd || subscription.freeUntil),
  }
}



function slugifyPropertyKeyword(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function buildPropertySearchHref(mode: PropertySearchMode, propertyTypeSlug: string, query: string) {
  const selectedMode = PROPERTY_SEARCH_MODES.find((item) => item.key === mode) || PROPERTY_SEARCH_MODES[0]
  const selectedType = propertyTypeSlug === 'all'
    ? null
    : PROPERTY_TYPE_MENU_ITEMS.find((item) => item.slug === propertyTypeSlug) ?? null
  const keywordSlug = slugifyPropertyKeyword(query)
  const typeSegment = selectedType ? `/${selectedType.slug}` : ''
  return keywordSlug ? `${selectedMode.basePath}${typeSegment}/${keywordSlug}` : `${selectedMode.basePath}${typeSegment}`
}

function buildAiSearchSuggestion(input: string): AiSearchSuggestion | null {
  const prompt = input.trim()
  if (prompt.length < 4) return null

  const lowerPrompt = prompt.toLowerCase()
  const mode: PropertySearchMode = lowerPrompt.includes('sewa') || lowerPrompt.includes('kontrak')
    ? 'sewa'
    : lowerPrompt.includes('baru') || lowerPrompt.includes('cluster')
      ? 'baru'
      : 'jual'
  const matchedType = PROPERTY_TYPE_MENU_ITEMS.find((item) => lowerPrompt.includes(item.label.toLowerCase()))
  const propertyTypeSlug = matchedType?.slug || 'all'
  const locationMatch = lowerPrompt.match(/\b(?:di|area|sekitar|dekat)\s+([a-z0-9\s.-]{3,})$/i)
  const query = (locationMatch?.[1] || prompt)
    .replace(/\b(rumah|apartemen|tanah|ruko|villa|gudang|kost|perkantoran|ruang usaha|dijual|disewa|sewa|jual|baru)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const budgetMatch = prompt.match(/(?:rp\s*)?(\d+(?:[,.]\d+)?)\s*(miliar|m|juta|jt)/i)
  const selectedMode = PROPERTY_SEARCH_MODES.find((item) => item.key === mode) || PROPERTY_SEARCH_MODES[0]
  const chips = [
    selectedMode.label,
    matchedType?.label || 'Semua tipe',
    query || 'Indonesia',
    budgetMatch ? `Budget ${budgetMatch[1]} ${budgetMatch[2]}` : '',
  ].filter(Boolean)
  const href = buildPropertySearchHref(mode, propertyTypeSlug, query)

  return {
    title: `AI membaca kebutuhanmu: ${matchedType?.label || 'properti'} ${selectedMode.label.toLowerCase()}`,
    description: `Mulai dari ${query || 'lokasi pilihan'} dengan filter ${matchedType?.label || 'semua tipe'}. Kamu masih bisa ubah sebelum mencari.`,
    chips,
    href,
    mode,
    propertyTypeSlug,
    query,
  }
}

function readRecentSearches(): PropertyRecentSearch[] {
  if (typeof window === 'undefined') return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PROPERTY_RECENT_SEARCH_STORAGE_KEY) || '[]') as PropertyRecentSearch[]
    const cleanItems = Array.isArray(parsed)
      ? parsed.filter((item) => item?.label && item?.meta && item?.href).slice(0, 3)
      : []
    return cleanItems
  } catch {
    return []
  }
}

function getMapboxCategory(placeType: string[] | undefined) {
  const type = placeType?.[0] || 'lokasi'
  const labels: Record<string, string> = {
    address: 'Alamat',
    poi: 'Tempat',
    neighborhood: 'Area',
    locality: 'Lokalitas',
    place: 'Kota',
    district: 'Kabupaten',
    region: 'Provinsi',
  }
  return labels[type] || 'Lokasi'
}

function getPropertyTypeIcon(slug: string) {
  if (slug === 'rumah') return Home
  if (slug === 'tanah') return MapPin
  return Building2
}

function isWhatsAppHref(href: string) {
  return href.startsWith('https://wa.me/') || href.startsWith('https://api.whatsapp.com/')
}

function PropertyPromoCanvas({ item, compact = false }: { item: PropertyHeroSlide | PropertyAdBanner; compact?: boolean }) {
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const width = compact ? AD_BANNER_WIDTH : HERO_POSTER_WIDTH
  const height = compact ? AD_BANNER_HEIGHT : HERO_POSTER_HEIGHT
  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return
    const update = () => setScale(frame.clientWidth / width)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(frame)
    return () => observer.disconnect()
  }, [width])
  return (
    <div ref={frameRef} className={`property-promo-frame${compact ? ' is-compact' : ''}`} style={{ aspectRatio: `${width} / ${height}` }}>
    <div className={`property-promo-canvas${compact ? ' is-compact' : ''}`} style={{ width, height, transform: `scale(${scale})` }}>
      <div className="property-promo-brand">
        <img src="/icons/blue-192.png" width="32" height="32" alt="" />
        <span>Dihaw<span>.co</span></span>
      </div>
      <small className="property-promo-size">{width} × {height} px</small>
      <span className="property-promo-badge">{item.badge}</span>
      <strong className="property-promo-title">{item.title}</strong>
      <p>{item.description}</p>
      {compact ? <span className="property-promo-action">
        {isWhatsAppHref(item.href) ? <img className="property-whatsapp-logo" src={WHATSAPP_LOGO_SRC} alt="" /> : null}
        {item.actionLabel}<ChevronRight size={18} />
      </span> : null}
    </div>
    </div>
  )
}

import { PropertyPagination } from '@/components/property/PropertyPagination'

export function HomeLandingPage({ locale, onLocaleChange }: HomeLandingPageProps) {
  const [databaseSlides, setDatabaseSlides] = useState<PropertyHeroSlide[]>([])
  const [databaseAdBanners, setDatabaseAdBanners] = useState<PropertyAdBanner[]>([])
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [activeAdBannerIndex, setActiveAdBannerIndex] = useState(0)
  const [loadedAdBannerImages, setLoadedAdBannerImages] = useState<Record<string, boolean>>({})
  const [searchMode, setSearchMode] = useState<PropertySearchMode>('jual')
  const [selectedPropertyTypeSlug, setSelectedPropertyTypeSlug] = useState('all')
  const [isPropertyTypeOpen, setIsPropertyTypeOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [aiSearchPrompt, setAiSearchPrompt] = useState('')
  const [aiSearchSuggestion, setAiSearchSuggestion] = useState<AiSearchSuggestion | null>(null)
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false)
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<MapboxSuggestion[]>([])
  const [recentSearches, setRecentSearches] = useState(readRecentSearches)
  const [agentOffset, setAgentOffset] = useState(0)
  const [agentNext, setAgentNext] = useState<number | null>(null)
  const [agentTotal, setAgentTotal] = useState(0)
  const [agentError, setAgentError] = useState('')
  const [agents, setAgents] = useState<PropertyAgentCard[]>([])
  const [isAgentsLoading, setIsAgentsLoading] = useState(true)
  const [featuredListings, setFeaturedListings] = useState<PropertyFeaturedListingCard[]>([])
  const [isFeaturedListingsLoading, setIsFeaturedListingsLoading] = useState(true)
  const [propertyArticles, setPropertyArticles] = useState<PropertyArticle[]>([])
  const [isPropertyArticlesLoading, setIsPropertyArticlesLoading] = useState(true)
  const propertyTypePickerRef = useRef<HTMLDivElement | null>(null)
  const searchFieldRef = useRef<HTMLDivElement | null>(null)
  const adBannerTrackRef = useRef<HTMLDivElement | null>(null)
  const articleTrackRef = useRef<HTMLDivElement | null>(null)
  const [articleScroll, setArticleScroll] = useState({ previous: false, next: false })
  useEffect(() => {
    const track = articleTrackRef.current
    if (!track) return
    const update = () => {
      const previous = track.scrollLeft > 2
      const next = track.scrollWidth - track.clientWidth - track.scrollLeft > 2
      setArticleScroll(current => current.previous === previous && current.next === next ? current : { previous, next })
    }
    update()
    track.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(track)
    return () => { track.removeEventListener('scroll', update); observer.disconnect() }
  }, [propertyArticles, isPropertyArticlesLoading])
  const scrollArticles = (direction: number) => {
    const track = articleTrackRef.current
    if (!track) return
    const card = track.querySelector<HTMLElement>('.property-info-article-card')
    const distance = (card?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(getComputedStyle(track).columnGap) || 0)
    track.scrollBy({ left: direction * distance, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }
  const heroSlides = useMemo(() => {
    if (databaseSlides.length === 0) {
      return [CONTACT_HERO_SLIDE]
    }
    const publishedSlides = databaseSlides.slice(0, 4)
    return [...publishedSlides, CONTACT_HERO_SLIDE]
  }, [databaseSlides])
  const adBanners = useMemo(() => {
    if (databaseAdBanners.length === 0) {
      return FALLBACK_AD_BANNERS
    }
    const mergedBanners = [...databaseAdBanners.slice(0, 7)]
    const addFallbackBanner = (fallbackBanner: PropertyAdBanner) => {
      const isDuplicate = mergedBanners.some((banner) => (
        banner.title.trim().toLowerCase() === fallbackBanner.title.trim().toLowerCase()
        || (banner.href.trim() && banner.href.trim() === fallbackBanner.href.trim())
      ))
      if (!isDuplicate) {
        mergedBanners.push(fallbackBanner)
      }
    }
    FALLBACK_AD_BANNERS.forEach((fallbackBanner) => {
      if (mergedBanners.length < 4) addFallbackBanner(fallbackBanner)
    })
    return mergedBanners.slice(0, 8)
  }, [databaseAdBanners])
  const activeSlide = heroSlides[activeSlideIndex] ?? CONTACT_HERO_SLIDE
  const ActiveSlideIcon = HERO_ICON_MAP[activeSlide.iconKey] ?? MessageCircle
  const activePosterUrl = useMemo(() => (
    activeSlide.renderMode === 'image' && activeSlide.imageUrl
      ? activeSlide.imageUrl
      : ''
  ), [activeSlide])

  usePageSeo({
    title: 'Dihaw.co - Cari Properti Dijual, Disewa, dan Properti Baru dengan AI',
    description:
      'Cari rumah, apartemen, tanah, ruko, gudang, villa, ruang usaha, dan properti baru di Indonesia. Bandingkan foto, harga, spesifikasi, map, area sekitar, dan hubungi pemasang via WhatsApp.',
    path: '/',
    keywords:
      'Dihaw.co, cari properti, AI property search, properti dijual, properti disewa, rumah dijual, rumah disewa, apartemen dijual, tanah dijual, ruko dijual, properti baru, pasang iklan properti',
    imageUrl: DEFAULT_SEO_IMAGE_URL,
    imageAlt: 'Dihaw.co marketplace listing properti',
    locale: 'id_ID',
    jsonLd: seoSchemas.home,
  })

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!propertyTypePickerRef.current?.contains(event.target as Node)) {
        setIsPropertyTypeOpen(false)
      }
      if (!searchFieldRef.current?.contains(event.target as Node)) {
        setIsSuggestionOpen(false)
      }
    }
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setIsPropertyTypeOpen(false)
      setIsSuggestionOpen(false)
    }
    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [])

  useEffect(() => {
    const keyword = searchQuery.trim()
    if (keyword.length < 2) {
      setSuggestions([])
      setIsSuggestionLoading(false)
      return undefined
    }

    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      setIsSuggestionLoading(true)
      const params = new URLSearchParams({
        access_token: MAPBOX_PUBLIC_TOKEN,
        country: 'id',
        language: 'id',
        limit: '5',
        autocomplete: 'true',
        types: 'address,poi,neighborhood,locality,place,district,region',
      })
      const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(keyword)}.json?${params.toString()}`
      void fetch(endpoint, { signal: controller.signal })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`Mapbox ${response.status}`))))
        .then((payload: { features?: Array<{ id?: string; text?: string; place_name?: string; place_type?: string[] }> }) => {
          const nextSuggestions = (payload.features || [])
            .map((feature, index) => ({
              id: feature.id || `${feature.place_name || feature.text || 'lokasi'}-${index}`,
              text: feature.text || feature.place_name || '',
              placeName: feature.place_name || feature.text || '',
              category: getMapboxCategory(feature.place_type),
            }))
            .filter((item) => item.placeName)
            .slice(0, 5)
          setSuggestions(nextSuggestions)
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('Failed to load Mapbox suggestions:', error)
          }
          setSuggestions([])
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsSuggestionLoading(false)
        })
    }, 280)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [searchQuery])

  useEffect(() => {
    if (heroSlides.length <= 1) return undefined
    const timer = window.setInterval(() => {
      setActiveSlideIndex((current) => (current + 1) % heroSlides.length)
    }, 5200)
    return () => window.clearInterval(timer)
  }, [heroSlides.length])

  useEffect(() => {
    if (adBanners.length <= 1) return undefined
    const timer = window.setInterval(() => {
      goToAdBanner(activeAdBannerIndex + 1)
    }, 4400)
    return () => window.clearInterval(timer)
  }, [activeAdBannerIndex, adBanners.length])

  useEffect(() => {
    let isMounted = true
    void loadPropertyHomeHeroSlides()
      .then((slides) => {
        if (!isMounted) return
        setDatabaseSlides(slides.map((slide) => ({
          renderMode: slide.renderMode,
          imageUrl: slide.imageUrl,
          badge: slide.badge,
          title: slide.title,
          description: slide.description,
          href: slide.href,
          actionLabel: slide.actionLabel,
          accent: slide.accent,
          iconKey: slide.iconKey,
          stats: [
            { label: 'Status', value: 'Aktif' },
            { label: 'Sumber', value: 'Database' },
            { label: 'Dihaw.co', value: 'Property' },
          ],
        })))
        setActiveSlideIndex(0)
      })
      .catch((error) => {
        console.warn('Failed to load property hero slides:', error)
      })
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    void loadPropertyAdBanners()
      .then((banners) => {
        if (!isMounted) return
        setDatabaseAdBanners(banners.map((banner) => ({
          renderMode: banner.renderMode,
          imageUrl: banner.imageUrl,
          badge: banner.badge,
          title: banner.title,
          description: banner.description,
          href: banner.href,
          actionLabel: banner.actionLabel,
          accent: banner.accent,
          iconKey: banner.iconKey,
        })))
        setActiveAdBannerIndex(0)
      })
      .catch((error) => {
        console.warn('Failed to load property ad banners:', error)
      })
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    setIsAgentsLoading(true)
    setAgentError('')
    const unsubscribe = onSnapshot(
      query(collection(propertyDb, PROPERTY_USERS_COLLECTION), pageOptions({offset: agentOffset, limit: 20})),
      (snapshot) => {
        const nextAgents = snapshot.docs
          .map((document) => buildAgentCard(document.id, document.data()))
          .filter((item): item is PropertyAgentCard => Boolean(item))

        setAgents(nextAgents)
        setAgentNext(snapshot.nextOffset)
        setAgentTotal(snapshot.total || 0)
        setIsAgentsLoading(false)
      },
      (error) => {
        setAgentError(error.message)
        console.warn('Failed to load property agents:', error)
        setAgents([])
        setIsAgentsLoading(false)
      },
    )
    return () => {
      unsubscribe()
    }
  }, [agentOffset])

  useEffect(() => {
    setIsFeaturedListingsLoading(true)
    const unsubscribe = onSnapshot(
      query(collection(propertyDb, PROPERTY_LISTINGS_COLLECTION), pageOptions({limit: 20, sort: 'recommended'})),
      (snapshot) => {
        const nextListings = snapshot.docs
          .map((document) => buildFeaturedListingCard(document.id, document.data()))

        setFeaturedListings(nextListings)
        setIsFeaturedListingsLoading(false)
      },
      (error) => {
        console.warn('Failed to load featured property listings:', error)
        setFeaturedListings([])
        setIsFeaturedListingsLoading(false)
      },
    )
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    setIsPropertyArticlesLoading(true)
    void listPropertyArticles({ limit: 20 })
      .then((result) => {
        if (!isMounted) return
        setPropertyArticles(result.items.slice(0, 20))
      })
      .catch((error) => {
        console.warn('Failed to load property articles:', error)
        if (isMounted) setPropertyArticles([])
      })
      .finally(() => {
        if (isMounted) setIsPropertyArticlesLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [])

  useHorizontalDrag('.property-home-hero-slide', (direction) => {
    setActiveSlideIndex((current) => (current + direction + heroSlides.length) % heroSlides.length)
  })

  const goToSlide = (nextIndex: number) => {
    setActiveSlideIndex((nextIndex + heroSlides.length) % heroSlides.length)
  }

  const goToAdBanner = (nextIndex: number) => {
    if (adBanners.length === 0) return
    const normalizedIndex = (nextIndex + adBanners.length) % adBanners.length
    const isLoopingToStart = activeAdBannerIndex === adBanners.length - 1 && normalizedIndex === 0
    setActiveAdBannerIndex(normalizedIndex)
    const targetCard = adBannerTrackRef.current?.querySelector<HTMLElement>(`[data-ad-banner-index="${normalizedIndex}"]`)
    if (targetCard && adBannerTrackRef.current) {
      adBannerTrackRef.current.scrollTo({
        left: isLoopingToStart ? 0 : targetCard.offsetLeft,
        behavior: isLoopingToStart ? 'auto' : 'smooth',
      })
    }
  }

  const renderFeaturedListingCard = (listing: PropertyFeaturedListingCard) => (
    <article key={listing.id} className="property-featured-listing-card">
      <a className="property-featured-listing-media" href={getListingHref(listing)} aria-label={`Lihat detail ${listing.title}`}>
        {listing.imageUrl ? (
          <SmartPropertyImage src={listing.imageUrl} alt={listing.title} tone="card" />
        ) : (
          <span>
            <Home size={34} />
          </span>
        )}
        <div className="property-featured-listing-badges">
          <span className="is-type">{getListingTypeLabel(listing.listingType)}</span>
          {isFeaturedListingActive(listing) ? <span className="is-featured">Featured</span> : null}
          {listing.isOfficial ? <span className="is-official">Official</span> : null}
        </div>
      </a>
      <div className="property-featured-listing-body">
        <strong>{formatCompactRupiah(listing.price)}</strong>
        <h3>{listing.title}</h3>
        <p><MapPin size={15} />{getListingLocation(listing)}</p>
        <div className="property-featured-listing-specs">
          <span><Ruler size={15} />LT {listing.landSize || '-'} m2</span>
          <span><Building2 size={15} />LB {listing.buildingSize || '-'} m2</span>
          <span><BedDouble size={15} />{listing.bedrooms || 0} KT</span>
          <span><Bath size={15} />{listing.bathrooms || 0} KM</span>
          {listing.carports ? <span><CarFront size={15} />{listing.carports} Carport</span> : null}
        </div>
        <a href={getListingHref(listing)}>
          Lihat detail
          <ChevronRight size={16} />
        </a>
      </div>
    </article>
  )

  const renderPropertyArticleCard = (article: PropertyArticle) => (
    <article key={article.id} className="property-info-article-card">
      <a className="property-info-article-media" href={`/artikel/${getPropertyArticleSlug(article)}`} aria-label={`Baca ${article.title}`}>
        {article.coverImageUrl ? (
          <ProgressiveImage src={article.coverImageUrl} alt={article.title} loading="lazy" />
        ) : (
          <span><Newspaper size={30} /></span>
        )}
        <b>Info Properti</b>
      </a>
      <div className="property-info-article-body">
        <div className="property-info-article-meta">
          <span><CalendarDays size={14} />{formatPropertyArticleDate(getPropertyArticleDateValue(article))}</span>
          <span><Clock3 size={14} />{estimatePropertyArticleReadMinutes(article.content)} menit</span>
        </div>
        <h3>{article.title}</h3>
        <p>{getPropertyArticleExcerpt(article)}</p>
        <a href={`/artikel/${getPropertyArticleSlug(article)}`}>
          Baca artikel
          <ChevronRight size={16} />
        </a>
      </div>
    </article>
  )

  const handleAdBannerScroll = () => {
    const track = adBannerTrackRef.current
    if (!track) return
    const cards = Array.from(track.querySelectorAll<HTMLElement>('[data-ad-banner-index]'))
    const closestCard = cards.reduce<{ index: number; distance: number } | null>((closest, card) => {
      const index = Number(card.dataset.adBannerIndex || 0)
      const distance = Math.abs(card.offsetLeft - track.scrollLeft)
      if (!closest || distance < closest.distance) {
        return { index, distance }
      }
      return closest
    }, null)
    if (closestCard && closestCard.index !== activeAdBannerIndex) {
      setActiveAdBannerIndex(closestCard.index)
    }
  }

  const selectedSearchMode = PROPERTY_SEARCH_MODES.find((item) => item.key === searchMode) || PROPERTY_SEARCH_MODES[0]
  const selectedPropertyTypeOption = PROPERTY_TYPE_OPTIONS.find((item) => item.slug === selectedPropertyTypeSlug) ?? PROPERTY_TYPE_OPTIONS[0]
  const selectedPropertyType = selectedPropertyTypeSlug === 'all'
    ? null
    : PROPERTY_TYPE_MENU_ITEMS.find((item) => item.slug === selectedPropertyTypeSlug) ?? null
  const SelectedPropertyTypeIcon = getPropertyTypeIcon(selectedPropertyTypeOption.slug)

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault()
    const keyword = searchQuery.trim()
    const href = buildPropertySearchHref(searchMode, selectedPropertyTypeSlug, keyword)
    const nextRecentItem = {
      label: keyword || selectedPropertyType?.label || selectedSearchMode.label,
      meta: `${selectedSearchMode.label} | ${selectedPropertyType?.label ?? 'Semua tipe'}`,
      href,
    }
    const nextRecentSearches = [
      nextRecentItem,
      ...recentSearches.filter((item) => item.href !== href && item.label.toLowerCase() !== nextRecentItem.label.toLowerCase()),
    ].slice(0, 3)
    setRecentSearches(nextRecentSearches)
    try {
      window.localStorage.setItem(PROPERTY_RECENT_SEARCH_STORAGE_KEY, JSON.stringify(nextRecentSearches))
    } catch {
      // Ignore storage failures.
    }
    setIsSuggestionOpen(false)
    window.location.assign(href)
  }

  const handleAiSearchSubmit = () => {
    const suggestion = buildAiSearchSuggestion(aiSearchPrompt)
    if (!suggestion) return
    setAiSearchSuggestion(suggestion)
    setSearchMode(suggestion.mode)
    setSelectedPropertyTypeSlug(suggestion.propertyTypeSlug)
    setSearchQuery(suggestion.query)
    setIsSuggestionOpen(false)
  }

  const applyAiSearchSuggestion = () => {
    if (!aiSearchSuggestion) return
    const selectedMode = PROPERTY_SEARCH_MODES.find((item) => item.key === aiSearchSuggestion.mode) || PROPERTY_SEARCH_MODES[0]
    const selectedType = aiSearchSuggestion.propertyTypeSlug === 'all'
      ? null
      : PROPERTY_TYPE_MENU_ITEMS.find((item) => item.slug === aiSearchSuggestion.propertyTypeSlug) ?? null
    const nextRecentItem = {
      label: aiSearchSuggestion.query || selectedType?.label || selectedMode.label,
      meta: `${selectedMode.label} | ${selectedType?.label ?? 'Semua tipe'} | AI`,
      href: aiSearchSuggestion.href,
    }
    const nextRecentSearches = [
      nextRecentItem,
      ...recentSearches.filter((item) => item.href !== aiSearchSuggestion.href && item.label.toLowerCase() !== nextRecentItem.label.toLowerCase()),
    ].slice(0, 3)
    setRecentSearches(nextRecentSearches)
    try {
      window.localStorage.setItem(PROPERTY_RECENT_SEARCH_STORAGE_KEY, JSON.stringify(nextRecentSearches))
    } catch {
      // Ignore storage failures.
    }
    window.location.assign(aiSearchSuggestion.href)
  }

  return (
    <main id="top" className="home-landing-page property-home" onClick={handleDelegatedInternalNavigation}>
      <PropertySiteHeader locale={locale} onLocaleChange={onLocaleChange} />

      <section className="property-home-search-section" aria-label="Pencarian properti">
        <div className="property-home-search-intro">
          <p>Dihaw.co / Properti untuk langkah berikutnya</p>
          <h1>Jual, beli, sewa.<br /><span>Temukan tempatmu.</span></h1>
          <p>Rumah, ruang usaha, atau investasi berikutnya. Mulai dari lokasi pilihanmu.</p>
        </div>
        <form className="property-home-search-panel" onSubmit={handleSearchSubmit} aria-label="Cari properti">
          <div className="property-home-search-tabs" role="tablist" aria-label="Pilih tipe pencarian">
            {PROPERTY_SEARCH_MODES.map((mode) => (
              <button
                key={mode.key}
                type="button"
                className={searchMode === mode.key ? 'is-active' : ''}
                onClick={() => setSearchMode(mode.key)}
                role="tab"
                aria-selected={searchMode === mode.key}
              >
                <mode.Icon size={16} />
                {mode.label}
              </button>
            ))}
          </div>
          <div className="property-home-search-field" ref={searchFieldRef}>
            <Search size={18} />
            <div className="property-home-type-picker" ref={propertyTypePickerRef}>
              <button
                type="button"
                className="property-home-type-trigger"
                onClick={() => setIsPropertyTypeOpen((current) => !current)}
                aria-label="Pilih jenis properti"
                aria-expanded={isPropertyTypeOpen}
              >
                <SelectedPropertyTypeIcon size={17} />
                <span>{selectedPropertyTypeOption.label}</span>
                <ChevronDown size={15} />
              </button>
              {isPropertyTypeOpen ? (
                <div className="property-home-type-menu" role="listbox" aria-label="Jenis properti">
                  {PROPERTY_TYPE_OPTIONS.map((item) => {
                    const OptionIcon = getPropertyTypeIcon(item.slug)
                    return (
                      <button
                        key={item.slug}
                        type="button"
                        className={selectedPropertyTypeSlug === item.slug ? 'is-selected' : ''}
                        onClick={() => {
                          setSelectedPropertyTypeSlug(item.slug)
                          setIsPropertyTypeOpen(false)
                        }}
                        role="option"
                        aria-selected={selectedPropertyTypeSlug === item.slug}
                      >
                        <OptionIcon size={16} />
                        <span>{item.label}</span>
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
            <div className="property-home-autocomplete">
              <input
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value)
                  setIsSuggestionOpen(true)
                }}
                onFocus={() => setIsSuggestionOpen(true)}
                placeholder="Lokasi, keyword, area, project, developer"
                aria-label="Lokasi, keyword, area, project, developer"
                autoComplete="off"
              />
              {isSuggestionOpen && searchQuery.trim().length >= 2 && (isSuggestionLoading || suggestions.length > 0) ? (
                <div className="property-home-suggestion-menu" role="listbox" aria-label="Saran lokasi">
                  {isSuggestionLoading ? (
                    <div className="property-home-suggestion-loading">
                      <Loader2 size={15} />
                      Mencari lokasi...
                    </div>
                  ) : null}
                  {suggestions.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSearchQuery(item.placeName)
                        setIsSuggestionOpen(false)
                      }}
                      role="option"
                    >
                      <MapPin size={16} />
                      <span>
                        <strong>{item.text}</strong>
                        <small>{item.placeName}</small>
                      </span>
                      <em>{item.category}</em>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button type="submit">Cari</button>
          </div>
          {recentSearches.length > 0 ? (
            <div className="property-home-recent-searches">
              <span>Pencarian Terakhir</span>
              <div>
                {recentSearches.slice(0, 3).map((item) => (
                  <a key={`${item.href}-${item.label}`} href={item.href}>
                    <strong>{item.label}</strong>
                    <small>{item.meta}</small>
                  </a>
                ))}
              </div>
            </div>
          ) : null}
          <div className="property-ai-search-card">
            <div>
              <span className="property-ai-badge"><Sparkles size={14} /> AI Property Search</span>
              <p>Tulis kebutuhan dengan bahasa biasa, AI bantu ubah jadi arah pencarian.</p>
            </div>
            <div className="property-ai-search-input">
              <input
                value={aiSearchPrompt}
                onChange={(event) => setAiSearchPrompt(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return
                  event.preventDefault()
                  const suggestion = buildAiSearchSuggestion(aiSearchPrompt)
                  if (!suggestion) return
                  setAiSearchSuggestion(suggestion)
                  setSearchMode(suggestion.mode)
                  setSelectedPropertyTypeSlug(suggestion.propertyTypeSlug)
                  setSearchQuery(suggestion.query)
                }}
                placeholder="Contoh: rumah dekat Bandung untuk keluarga, budget 800 juta"
                aria-label="Cari properti dengan bantuan AI"
              />
              <button type="button" onClick={handleAiSearchSubmit} disabled={aiSearchPrompt.trim().length < 4}>
                Baca kebutuhan
              </button>
            </div>
            {aiSearchSuggestion ? (
              <article className="property-ai-search-result">
                <strong>{aiSearchSuggestion.title}</strong>
                <p>{aiSearchSuggestion.description}</p>
                <div>
                  {aiSearchSuggestion.chips.map((chip) => <span key={chip}>{chip}</span>)}
                </div>
                <button type="button" onClick={applyAiSearchSuggestion}>Terapkan & cari</button>
              </article>
            ) : null}
          </div>
        </form>
      </section>

      <section className="property-home-hero-slider" aria-label="Sorotan Dihaw.co">
        <article className={`property-home-hero-slide is-${activeSlide.accent}`}>
          <span className="property-sr-only">{activeSlide.title}</span>
          <p className="property-sr-only">{activeSlide.description}</p>
          <div className="property-home-hero-frame">
            {activePosterUrl ? <ProgressiveImage
              key={`${activeSlideIndex}-${activeSlide.title || activeSlide.imageUrl}`}
              className="property-home-hero-poster"
              src={activePosterUrl}
              width={HERO_POSTER_WIDTH}
              height={HERO_POSTER_HEIGHT}
              alt={`${activeSlide.badge}. ${activeSlide.title || activeSlide.actionLabel}`}
            /> : <PropertyPromoCanvas item={activeSlide} />}
          </div>
          <a className="property-home-hero-cta" href={activeSlide.href} target={activeSlide.href.startsWith('https://') ? '_blank' : undefined} rel={activeSlide.href.startsWith('https://') ? 'noreferrer' : undefined}>
            {isWhatsAppHref(activeSlide.href)
              ? <img className="property-whatsapp-logo" src={WHATSAPP_LOGO_SRC} alt="" />
              : <ActiveSlideIcon size={18} />}
            {activeSlide.actionLabel}
            <ChevronRight size={18} />
          </a>
          <div className="property-home-hero-controls" aria-label="Kontrol slider">
            <button className="property-home-hero-nav is-prev" type="button" onClick={() => goToSlide(activeSlideIndex - 1)} aria-label="Slide sebelumnya">
              <ChevronLeft size={19} />
            </button>
            <div className="property-home-hero-dots" role="tablist" aria-label="Pilih slide">
              {heroSlides.map((slide, index) => (
                <button
                  key={`${slide.title}-${index}`}
                  type="button"
                  className={index === activeSlideIndex ? 'is-active' : ''}
                  onClick={() => goToSlide(index)}
                  aria-label={`Buka slide ${index + 1}: ${slide.badge}`}
                  aria-selected={index === activeSlideIndex}
                  role="tab"
                />
              ))}
            </div>
            <button className="property-home-hero-nav is-next" type="button" onClick={() => goToSlide(activeSlideIndex + 1)} aria-label="Slide berikutnya">
              <ChevronRight size={19} />
            </button>
          </div>
        </article>
      </section>

      <section className="property-featured-listing-section" aria-label="Property unggulan">
        <div className="property-agent-section-head">
          <div>
            <h2>Properti pilihan</h2>
          </div>
          <a href="/jual">
            Lihat semua properti
            <ChevronRight size={18} />
          </a>
        </div>
        <div className="property-featured-listing-row">
          {isFeaturedListingsLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <article key={`featured-listing-skeleton-${index}`} className="property-featured-listing-card property-featured-listing-skeleton">
                <span className="property-skeleton-media" />
                <div className="property-featured-listing-body">
                  <span className="property-skeleton-line is-title" />
                  <span className="property-skeleton-line" />
                  <span className="property-skeleton-line is-medium" />
                  <span className="property-skeleton-pill is-wide" />
                </div>
              </article>
            ))
          ) : featuredListings.length === 0 ? (
            <article className="property-featured-listing-empty">
              <span><Home size={24} /></span>
              <h3>Property unggulan segera hadir.</h3>
              <p>Listing pilihan akan tampil di sini setelah tersedia.</p>
            </article>
          ) : (
            featuredListings.map(renderFeaturedListingCard)
          )}
        </div>
      </section>

      <section className="property-ad-banner-section" aria-label="Iklan properti Dihaw.co">
        <div className="property-ad-banner-track" ref={adBannerTrackRef} onScroll={handleAdBannerScroll}>
          {adBanners.map((banner, index) => {
            const bannerImageSrc = banner.renderMode === 'image' && banner.imageUrl ? banner.imageUrl : ''
            const bannerImageKey = `${bannerImageSrc}-${index}`
            const isBannerImageLoaded = !bannerImageSrc || Boolean(loadedAdBannerImages[bannerImageKey])
            const markBannerImageReady = () => {
              setLoadedAdBannerImages((current) => (
                current[bannerImageKey] ? current : { ...current, [bannerImageKey]: true }
              ))
            }
            return (
              <a
                key={`${banner.title || banner.imageUrl || banner.actionLabel}-${index}`}
                className={`property-ad-banner-card ${isBannerImageLoaded ? 'is-loaded' : 'is-loading'}`}
                data-ad-banner-index={index}
                href={banner.href}
                target={banner.href.startsWith('https://') ? '_blank' : undefined}
                rel={banner.href.startsWith('https://') ? 'noreferrer' : undefined}
              >
                {bannerImageSrc ? <ProgressiveImage
                  src={bannerImageSrc}
                  width={AD_BANNER_WIDTH}
                  height={AD_BANNER_HEIGHT}
                  alt={`${banner.badge}. ${banner.title || banner.actionLabel}`}
                  decoding="async"
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  onError={markBannerImageReady}
                  onLoad={markBannerImageReady}
                /> : <PropertyPromoCanvas item={banner} compact />}
              </a>
            )
          })}
        </div>
        <div className="property-ad-banner-dots" aria-label="Posisi banner">
          {adBanners.map((banner, index) => (
            <button
              key={`${banner.title}-dot-${index}`}
              type="button"
              className={index === activeAdBannerIndex ? 'is-active' : ''}
              onClick={() => goToAdBanner(index)}
              aria-label={`Buka banner ${index + 1}`}
            />
          ))}
        </div>
      </section>

      <section className="property-agent-landing-section" id="agent">
        <div className="property-agent-section-head">
          <div>
            <h2>Agen Properti Unggulan</h2>
          </div>
          <a href="/agent" onClick={(event) => handlePropertyRegisterModalNavigation(event, 'agent')}>
            Lihat semua
            <ChevronRight size={18} />
          </a>
        </div>
        <div className="property-agent-scroll-row" aria-label="Daftar agent properti">
          <article className="property-agent-card is-plan">
            <div className="property-agent-card-badges">
              <span>Agent Property</span>
            </div>
            <div className="property-agent-price-stack">
              {IS_AGENT_FREE_MODE ? <del>{formatRupiah(PROPERTY_AGENT_BILLING.amount)}</del> : null}
              <strong>{IS_AGENT_FREE_MODE ? 'Rp0' : formatRupiah(PROPERTY_AGENT_BILLING.amount)}</strong>
              <small>{IS_AGENT_FREE_MODE ? `Gratis sampai ${AGENT_FREE_UNTIL_LABEL}` : 'per tahun'}</small>
            </div>
            <p>{PROPERTY_AGENT_BILLING.listingLimit} listing/tahun. Profile agent profesional.</p>
            <a href="/agent" onClick={(event) => handlePropertyRegisterModalNavigation(event, 'agent')}>
              <UsersRound size={18} />
              Login sebagai Agen
            </a>
          </article>
          {isAgentsLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <article key={`agent-skeleton-${index}`} className="property-agent-card property-agent-card-skeleton">
                <div className="property-agent-skeleton-top">
                  <span className="property-skeleton-avatar" />
                  <span className="property-skeleton-pill" />
                </div>
                <span className="property-skeleton-line is-title" />
                <span className="property-skeleton-line is-medium" />
                <span className="property-skeleton-line is-short" />
                <span className="property-skeleton-pill is-wide" />
              </article>
            ))
          ) : agentError ? <p role="alert">{agentError}</p> : agents.length === 0 ? (
            null
          ) : (
            agents.map((agent) => (
              <article key={agent.id} className="property-agent-card">
                <div className="property-agent-card-media">
                  <div className="property-agent-avatar">
                    {agent.photoUrl ? <ProgressiveImage src={agent.photoUrl} alt={agent.fullName} loading="lazy" /> : <UsersRound size={28} />}
                  </div>
                  {agent.companyLogoUrl ? (
                    <div className="property-agent-company-logo">
                      <img src={agent.companyLogoUrl} alt={agent.companyName || 'Logo company'} loading="lazy" />
                    </div>
                  ) : null}
                </div>
                <span>Verified Agent</span>
                <h3>{agent.fullName}</h3>
                <p>{agent.companyName || `${agent.listingLimit} kuota listing tahunan`}</p>
                <a href="/agent" onClick={(event) => handlePropertyRegisterModalNavigation(event, 'agent')}>
                  Lihat profile
                  <ChevronRight size={16} />
                </a>
              </article>
            ))
          )}
        </div>
        <PropertyPagination offset={agentOffset} nextOffset={agentNext} total={agentTotal} loading={isAgentsLoading} onChange={setAgentOffset} />
      </section>

      <section className="property-why-section" aria-label="Kenapa Dihaw.co">
        <div className="property-why-head">
          <span>Kenapa Dihaw.co?</span>
          <h2>Langkah berikutnya, lebih mudah</h2>
        </div>
        <div className="property-why-grid">
          {WHY_DIHAW_ITEMS.map((item) => {
            const Icon = item.Icon
            return (
              <article key={item.title} className="property-why-card">
                <span>
                  <Icon size={24} />
                </span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <a href={item.href}>
                  {item.actionLabel}
                  <ChevronRight size={16} />
                </a>
              </article>
            )
          })}
        </div>
      </section>

      <section className="property-info-article-section" aria-label="Info Properti">
        <div className="property-agent-section-head">
          <div>
            <span className="property-section-kicker"><BookOpenText size={15} />Info Properti</span>
            <h2>Wawasan properti</h2>
          </div>
          <div className="property-info-article-actions">
            <a href="/artikel">Lihat semua artikel<ChevronRight size={18} /></a>
            {!isPropertyArticlesLoading && propertyArticles.length > 1 && <div className="property-info-article-navigation" aria-label="Navigasi artikel">
              <button type="button" aria-label="Artikel sebelumnya" aria-controls="home-property-articles" disabled={!articleScroll.previous} onClick={() => scrollArticles(-1)}><ChevronLeft size={20} /></button>
              <button type="button" aria-label="Artikel berikutnya" aria-controls="home-property-articles" disabled={!articleScroll.next} onClick={() => scrollArticles(1)}><ChevronRight size={20} /></button>
            </div>}
          </div>
        </div>
        <div ref={articleTrackRef} id="home-property-articles" className="property-info-article-row" tabIndex={0} role="region" aria-label="Daftar wawasan properti, geser untuk melihat artikel lainnya" aria-busy={isPropertyArticlesLoading}>
          {isPropertyArticlesLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <article key={`property-article-skeleton-${index}`} className="property-info-article-card is-skeleton">
                <span className="property-skeleton-media" />
                <div className="property-info-article-body">
                  <span className="property-skeleton-line is-title" />
                  <span className="property-skeleton-line" />
                  <span className="property-skeleton-line is-medium" />
                </div>
              </article>
            ))
          ) : propertyArticles.length === 0 ? (
            <article className="property-info-article-empty">
              <span><Newspaper size={24} /></span>
              <h3>Info properti sedang disiapkan.</h3>
              <p>Nanti bagian ini berisi bacaan ringkas seputar rumah, lokasi, KPR, dan cara membaca peluang properti.</p>
            </article>
          ) : (
            propertyArticles.map(renderPropertyArticleCard)
          )}
        </div>
      </section>

      <section className="property-home-faq" aria-labelledby="home-faq-title">
        <div>
          <header>
            <h2 id="home-faq-title">Pertanyaan yang sering diajukan</h2>
            <p>Seputar pencarian properti, pemasangan listing, dan layanan Dihaw.co.</p>
            <a href="/faq">Lihat semua<ChevronRight size={18} /></a>
          </header>
          <PropertyFaqList items={HOME_PROPERTY_FAQS} idPrefix="home-faq" />
        </div>
      </section>

      <PropertySiteFooter />
    </main>
  )
}
